import { Frame, Head, Label, Node, fx } from './frame';
import { useStrings } from '../../../state/useLocale';
import type { FigureProps } from './index';

/**
 * The two figures about other people's work.
 *
 * `remote` says that there are two repositories and that nothing crosses
 * between them until you name a direction. `conflict` says that a conflict is
 * not damage: it is git writing both answers into the file and declining to
 * choose. Both drawings are careful to show the second copy still existing —
 * a push does not empty the local repository, and resolving does not lose the
 * version you did not pick, it just stops printing it.
 *
 * `origin`, `git push`, `git fetch`, the three conflict markers and the two
 * lines of code are the machine's and stay. The words about them — `local`,
 * `resolved`, the caption — come from the catalogue. See frame.tsx, `Label`.
 */

/* ── remote ──────────────────────────────────────────────────────────────────
   Two repositories, two arrows, one direction each. The green commit is the
   one being shared, and it is green in both panels at rest, because after a
   push it genuinely exists twice. The fetch arrow is drawn ghosted: it is the
   direction available, not the direction taken. */

export function RemoteFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;

  return (
    <Frame name="remote" active={active}>
      <g className="fx-in" style={fx(0)}>
        <rect className="box" x={12} y={40} width={110} height={76} rx={2} />
        <Label className="lbl-ink" x={22} y={58} at="left">
          {f.local}
        </Label>
      </g>

      <g className="fx-in" style={fx(100)}>
        <rect className="box" x={198} y={40} width={110} height={76} rx={2} />
        <text className="lbl-ink" x={208} y={58}>
          origin
        </text>
      </g>

      <g className="fx-rise" style={fx(180)}>
        <path className="edge" d="M32 92 H80" />
        <Node x={32} y={92} r={5} kind="dim" />
        <Node x={56} y={92} r={5} kind="dim" />
        <Node x={80} y={92} r={5} kind="signal" />
      </g>

      <g className="fx-rise" style={fx(280)}>
        <path className="edge" d="M218 92 H242" />
        <Node x={218} y={92} r={5} kind="dim" />
        <Node x={242} y={92} r={5} kind="dim" />
      </g>

      <g className="fx-in" style={fx(460)}>
        <text className="lbl-ink" x={158} y={66} textAnchor="middle">
          git push
        </text>
        <path className="wire bright fx-draw" style={fx(520, { '--t': '220ms', '--len': 70 })} d="M128 74 H184" />
        <Head className="bright fx-in" style={fx(700)} x={190} y={74} dir="right" />
      </g>

      {/* The same commit, arriving in the other repository. */}
      <Node
        className="fx-slide"
        style={fx(720, { '--t': '400ms', '--dx': '-186px' })}
        x={266}
        y={92}
        r={5}
        kind="signal"
      />
      <path className="edge fx-draw" style={fx(1040, { '--t': '160ms', '--len': 34 })} d="M242 92 H266" />

      <g className="fx-in" style={fx(1120)}>
        <path className="wire ghost" d="M192 108 H136" />
        <Head className="ghost" x={130} y={108} dir="left" />
        <text className="lbl-dim" x={158} y={124} textAnchor="middle">
          git fetch
        </text>
      </g>
    </Frame>
  );
}

/* ── conflict ────────────────────────────────────────────────────────────────
   One file, both versions, and the three lines git inserted to keep them
   apart. Resolving is drawn as what it is — the markers go, one version stays
   — rather than as a button being pressed. The markers recede at the end
   instead of vanishing, because on screen they are still text the learner has
   to delete by hand. */

export function ConflictFigure({ active }: FigureProps) {
  const f = useStrings().warmup.fig;

  return (
    <Frame name="conflict" active={active}>
      <rect className="well fx-in" style={fx(0)} x={14} y={18} width={160} height={84} rx={2} />

      {/* Everything git wrote in, in one group, so the last beat recedes it. */}
      <g className="fx-dim" style={fx(980, { '--t': '320ms' })} opacity={0.32}>
        <g className="fx-in" style={fx(120)}>
          <text className="lbl-ghost" x={22} y={30}>
            {'<<<<<<< HEAD'}
          </text>
          <text className="lbl-ghost" x={22} y={62}>
            =======
          </text>
          <text className="lbl-ghost" x={22} y={94}>
            {'>>>>>>> feature'}
          </text>
        </g>
      </g>

      <text className="fx-rise" style={fx(250)} x={30} y={46}>
        total = sub
      </text>
      <text className="lbl-ink fx-rise" style={fx(400)} x={30} y={78}>
        total = sub + tax
      </text>

      <path className="wire bright fx-draw" style={fx(620, { '--t': '200ms', '--len': 24 })} d="M178 60 H192" />
      <Head className="bright fx-in" style={fx(780)} x={196} y={60} dir="right" />

      <g className="fx-rise" style={fx(760, { '--dx': '-8px' })}>
        <Label className="lbl-dim" x={200} y={38} at="left">
          {f.resolved}
        </Label>
        {/* Wide enough for the line at the *fallback* mono's advance, not
            Space Mono's. The face is fetched from a CDN and the stack behind
            it is wider; sized to the 0.6em ideal this chip overflowed its own
            border the moment the font did not arrive. */}
        <rect className="box-sig" x={200} y={44} width={116} height={32} rx={2} />
      </g>
      <text className="lbl-ink fx-in" style={fx(880)} x={205} y={64}>
        total = sub + tax
      </text>

      <Label className="lbl-dim fx-in" style={fx(1080)} x={14} y={124} at="left">
        {f.pickOne}
      </Label>
    </Frame>
  );
}
